/*eslint react-hooks/exhaustive-deps: "off"*/
import React, { Fragment, useCallback, useMemo, useRef, useState } from "react";
import { Button, Drawer, Icon, Table, Tabs } from "antd";
import MapBase from "./MapBase";
import { MapPropsProvider } from "./MapPropsContext";
import useMapProps from "./useMapProps";
import WidgetContainer from "./widgets/WidgetContainer";
import WidgetRadioBroad from "./widgets/WidgetRadioBroad";
import {
  getApiUrl,
  getLastDomByClassName,
  makeTableSorterFn,
  rgbToArray,
} from "../../utils";
import WidgetLegend from "./widgets/WidgetLegend";
import WidgetRadioHighway from "./widgets/WidgetRadioHighway";
import WidgetTabs from "./widgets/WidgetTabs";
import "./MapRadioStats.scss";
import WidgetToolbar from "./widgets/WidgetToolbar";
import usePortal from "react-useportal";
import { TRANSPARENT_COLOR } from "../../utils/Palette";
import {
  TI_HIGHWAY_PATH_MODAL_CLOSE_BUTTON,
  TI_SHOW_LIST_BUTTON,
  WELL_KNOWN_POSITION,
} from "../../Constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";
import { getAsisExcelList } from "../../api";
import {
  StyledButton,
  StyledDarkerButton,
  StyledDisabledButton,
} from "../../styled-components/Button";
import StyledTabContainer from "../../styled-components/Tab";
import StyledTableHeader from "../../styled-components/StyledTableHeader";

const MapRadioStats = () => {
  const mapProps = useMapProps({
    INITIAL_VIEW_STATE: {
      ...WELL_KNOWN_POSITION.세종특별자치시,
      zoom: 8,
    },
    INITIAL_WIDGET_STATE: {
      broad_name_list: ["KBS", "KBS Clasic FM"],
      road_name_list: ["1"],
      service_nm: "KBS Clasic FM",
      road_no: 1,
    },
    INITIAL_GEOJSON_POLYGON_LAYER_PROPS: {
      getHoverHtml: (feature, mapDistrictStats) => {
        const {
          properties: { pk, name },
        } = feature;
        const { channelDict } = mapDistrictStats;
        if (!channelDict) return name;
        const channel = channelDict[pk];
        if (!channel) return name;
        const { BROADAREA, BROD_FREQ_NM } = channel;
        return `${BROADAREA} : ${BROD_FREQ_NM}`;
      },
      getLevelFillColor: (feature, mapDistrictStats) => {
        if (!feature) return TRANSPARENT_COLOR;
        const {
          properties: { pk },
        } = feature;
        const { channelDict, colorDict } = mapDistrictStats;
        if (!channelDict) return TRANSPARENT_COLOR;
        const channel = channelDict[pk];
        if (!channel) return TRANSPARENT_COLOR;

        const { BROD_FREQ_NM } = channel;
        const { COLOR } = colorDict[BROD_FREQ_NM];
        return [...rgbToArray(COLOR).slice(0, 3), 200];
      },
    },
  });

  const {
    mapDistrictStats,
    setMapDistrictStats,
    isChangedMapDistrictStats,
    filteredPointsResponseList,
  } = mapProps;

  const { channelDict, colorDict, originalChannelDict } = mapDistrictStats;

  const [serviceName, setServiceName] = useState("KBS Clasic FM");
  const [radioChannelDataForHighway, setRadioChannelDataForHighway] = useState(
    []
  );

  const [isVisibleTable, setIsVisibleTable] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const drawerRef = useRef();

  const setRadioChannelData = useCallback(
    (newMapDistrictStats) => {
      if (isChangedMapDistrictStats(newMapDistrictStats))
        setMapDistrictStats(newMapDistrictStats);
    },
    [isChangedMapDistrictStats, setMapDistrictStats]
  );

  const integratedLayer = filteredPointsResponseList.flat();

  // eslint-disable-next-line no-unused-vars
  const uidArray = useMemo(() => {
    return integratedLayer.map(({ uid }) => uid);
  }, [filteredPointsResponseList]);

  const fetchUrl = getApiUrl({ type: "type1" });

  const downloadXlsx = useCallback(() => {
    setIsDownloading(true);

    let formData = new FormData();
    formData.append("excelUrl", "/gis/getRadioServiceChannelPagedListExcel.do");
    formData.append("serviceNm", serviceName);
    formData.append("sortName", "");
    formData.append("orderBy", "");
    formData.append("rows", 65000);
    const excelFileName = "지역별 주파수 찾기.xls";

    getAsisExcelList({ formData, excelFileName })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        setIsDownloading(false);
      });
  }, [fetchUrl, serviceName]);

  const { Portal } = usePortal({
    bindTo: getLastDomByClassName("toolbar-show-list"),
  });

  const isExistChannelDict = channelDict && Object.keys(channelDict).length > 0;

  const modalExportExcelButton = useRef();

  const [selectedHighway, setSelectedHighway] = useState("");

  return (
    <MapPropsProvider value={mapProps}>
      <MapBase
        isHideControlOnClustering={true}
        isHideMeasureControl={true}
        isHideWidgetMapLayerTypes={true}
      >
        <WidgetContainer tabIndex={106}>
          <div>
            <WidgetRadioBroad
              setServiceName={setServiceName}
              setRadioChannelData={setRadioChannelData}
            />
            <WidgetRadioHighway
              setSelectedHighway={setSelectedHighway}
              disabled={!isExistChannelDict}
              setRadioChannelDataForHighway={setRadioChannelDataForHighway}
              serviceName={serviceName}
            />
          </div>
        </WidgetContainer>

        {radioChannelDataForHighway.length > 0 && (
          <StyledTabContainer style={{ width: 700 }}>
            <div style={{ display: "block" }}>
              <div
                style={{ padding: "10px", fontWeight: 500, color: "#1890ff" }}
              >
                {selectedHighway} 경로 탐색
              </div>
              <div
                style={{ backgroundColor: "white" }}
                className={"radio-channel-data-for-highway"}
              >
                <table style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      {radioChannelDataForHighway.map(
                        ({ BROADAREA }, index) => (
                          <th key={index}>{BROADAREA}</th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {radioChannelDataForHighway.map(
                        ({ BROD_FREQ_NM }, index) => (
                          <td key={index}>{BROD_FREQ_NM}</td>
                        )
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>
              <StyledButton
                onClick={() => setRadioChannelDataForHighway([])}
                style={{ margin: 5 }}
                tabIndex={TI_HIGHWAY_PATH_MODAL_CLOSE_BUTTON}
              >
                닫기
              </StyledButton>
            </div>
          </StyledTabContainer>
        )}

        <WidgetLegend
          fillColorLegend={
            colorDict &&
            Object.values(colorDict).map(({ COLOR, BROD_FREQ_NM }) => [
              COLOR,
              BROD_FREQ_NM,
            ])
          }
          titleColorLegend={"지역별 주파수"}
        />

        <Portal>
          {!isExistChannelDict ? (
            <StyledDisabledButton disabled tabIndex={TI_SHOW_LIST_BUTTON}>
              목록 보기
            </StyledDisabledButton>
          ) : (
            <StyledDarkerButton
              // disabled={!isExistChannelDict}
              onClick={() => {
                setIsVisibleTable((visible) => !visible);
              }}
              ref={drawerRef}
              tabIndex={TI_SHOW_LIST_BUTTON}
            >
              목록 보기
            </StyledDarkerButton>
          )}
          {/* <Button
            disabled={!isExistChannelDict}
            onClick={() => setIsVisibleTable((visible) => !visible)}
          >
            목록 보기
          </Button> */}
        </Portal>

        {isExistChannelDict && (
          <Drawer
            placement={"bottom"}
            height={300 + 200}
            visible={isVisibleTable}
            closable={false}
            bodyStyle={{ padding: 12 }}
          >
            <div>
              <div>
                <StyledTableHeader>
                  <span>
                    {Object.values(originalChannelDict).length > 0
                      ? `지역별 주파수 찾기 (총 검색 : ${
                          Object.values(originalChannelDict).length
                        }건)`
                      : "지역별 주파수 찾기 (총 검색 : 0건)"}
                  </span>

                  {fetchUrl && (
                    <div>
                      <StyledButton
                        onClick={downloadXlsx}
                        style={{ marginRight: 8 }}
                        isActive
                        disabled={!fetchUrl || isDownloading}
                        ref={modalExportExcelButton}
                      >
                        {isDownloading && <Icon type="loading" />}
                        {!isDownloading && (
                          <FontAwesomeIcon icon={faFileExcel} />
                        )}
                        <span style={{ marginLeft: 8 }}>엑셀 내보내기</span>
                      </StyledButton>
                      <StyledButton
                        onClick={() => {
                          drawerRef.current.focus();
                          setIsVisibleTable(false);
                        }}
                      >
                        닫기
                      </StyledButton>
                    </div>
                  )}
                </StyledTableHeader>
              </div>

              <Table
                dataSource={
                  originalChannelDict &&
                  Object.values(originalChannelDict).map((channel, index) => ({
                    ...channel,
                    index: index + 1,
                  }))
                }
                ellipsis={true}
                bordered={true}
                tableLayout={"fixed"}
                size={"small"}
                columns={[
                  {
                    title: "순번",
                    dataIndex: "index",
                    key: "index",
                    width: 75,
                    align: "center",
                    // sorter: makeTableSorterFn("index"),
                    fixed: "left",
                  },
                  {
                    title: "방송사",
                    dataIndex: "BROAD_NM",
                    key: "BROAD_NM",
                    // sorter: makeTableSorterFn("BROAD_NM"),
                    align: "center",
                  },
                  {
                    title: "채널명",
                    dataIndex: "SERVICE_NM",
                    key: "SERVICE_NM",
                    // sorter: makeTableSorterFn("SERVICE_NM"),
                    align: "center",
                  },
                  {
                    title: "주파수",
                    dataIndex: "BROD_FREQ_NM",
                    key: "BROD_FREQ_NM",
                    align: "center",
                    // sorter: makeTableSorterFn("BROD_FREQ_NM"),
                  },
                  {
                    title: "지역",
                    dataIndex: "BROADAREA",
                    key: "BROADAREA",
                    // sorter: makeTableSorterFn("BROADAREA"),
                    align: "center",
                  },
                ]}
                rowKey={"RN"}
                // scroll={{ y: 300 }}
                pagination={{
                  defaultPageSize: 20,
                  showSizeChanger: true,
                  pageSizeOptions: ["20", "50", "100"],
                }}
              />
              {/* </Tabs.TabPane>
            </Tabs> */}
            </div>
          </Drawer>
        )}
      </MapBase>
      <WidgetToolbar />
    </MapPropsProvider>
  );
};

const STYLE_SUBMIT = {
  position: "absolute",
  top: 2,
  right: 2,
  padding: "10px 12px",
};

export default MapRadioStats;
